import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, AlertCircle, Check, Clock } from "lucide-react";
import { TARIFS, SITE_INFO, computeDeposit } from "../mock";
import { createBooking, getBookedDates, getBookedSlots, getBlockedDates, getBlockedSlots, verifyGiftCard } from "../api";
import { useToast } from "../hooks/use-toast";
import { useLocation, useNavigate } from "react-router-dom";

const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const dayNames = ["L", "M", "M", "J", "V", "S", "D"];

// Time slots 7h to 22h
// Time slots dynamically computed based on the selected date.
// From July onwards (summer schedule), start at 6h. Otherwise start at 7h.
// End is always 22h. Returns ["HH:00", ...] hourly slots.
const getTimeSlotsForDate = (date) => {
  if (!date) return [];
  const month = date.getMonth(); // 0=Jan, 6=July
  const startHour = month >= 6 ? 6 : 7;
  const endHour = 22;
  const count = endHour - startHour + 1;
  return Array.from({ length: count }, (_, i) => `${String(i + startHour).padStart(2, '0')}:00`);
};

export default function Reservation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const initialPrestation = location.state?.prestation || "shooting";

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [step, setStep] = useState("date"); // date | time | form | confirm
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    prestation: initialPrestation,
    name: "", email: "", phone: "",
    giftCode: "", payment: "virement"
  });
  const [booking, setBooking] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);

  useEffect(() => {
    Promise.all([
      getBookedDates(), getBookedSlots(), getBlockedDates(), getBlockedSlots()
    ]).then(([bd, bs, bl, bls]) => {
      setBookedDates(bd); setBookedSlots(bs); setBlockedDates(bl); setBlockedSlots(bls);
    }).catch(() => {});
  }, []);

  // Debounced gift card verification
  const [giftCheck, setGiftCheck] = useState(null); // null | {valid, used, pending, amount, prestationTitle}
  useEffect(() => {
    const code = (form.giftCode || "").trim().toUpperCase();
    if (!code || code.length < 6) { setGiftCheck(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await verifyGiftCard(code);
        setGiftCheck(res);
      } catch { setGiftCheck(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.giftCode]);

  const isDayFullyBlocked = (d) => {
    const date = new Date(d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    const key = date.toISOString().split('T')[0];
    return blockedDates.includes(key);
  };

  // Returns availability count of time slots for a date
  const getAvailableSlotsCount = (date) => {
    const key = date.toISOString().split('T')[0];
    const slots = getTimeSlotsForDate(date);
    return slots.filter(t => {
      if (blockedSlots.some(s => s.date === key && s.time === t)) return false;
      if (bookedSlots.some(s => s.date === key && s.time === t)) return false;
      return true;
    }).length;
  };

  const month = currentDate.getMonth();
  const year = currentDate.getFullYear();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();

  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));

  const selectedTarif = TARIFS.find(t => t.id === form.prestation);
  const deposit = selectedTarif ? computeDeposit(selectedTarif) : 0;

  const handleSelectDate = (date) => {
    if (!date || isDayFullyBlocked(date)) return;
    if (getAvailableSlotsCount(date) === 0) return;
    setSelectedDate(date);
    setSelectedTime(null);
    setStep("time");
  };

  const isSlotAvailable = (date, time) => {
    const key = date.toISOString().split('T')[0];
    if (blockedSlots.some(s => s.date === key && s.time === time)) return false;
    if (bookedSlots.some(s => s.date === key && s.time === time)) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        date: selectedDate.toISOString().split('T')[0],
        dateFormatted: selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        time: selectedTime,
        amount: selectedTarif.price,
        deposit: deposit,
        prestationTitle: selectedTarif.title
      };
      const newBooking = await createBooking(payload);
      setBooking(newBooking);
      setStep("confirm");
      if (newBooking.paid_by_gift_card) {
        toast({ title: "🎉 Réservation confirmée !", description: `Votre séance est définitivement bloquée grâce à votre carte cadeau.` });
      } else {
        toast({ title: "Réservation envoyée !", description: `Consultez votre email pour régler l'acompte de ${deposit}€.` });
      }
    } catch (err) {
      toast({ title: "Erreur", description: err.response?.data?.detail || "Impossible d'envoyer la réservation", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const changeMonth = (dir) => setCurrentDate(new Date(year, month + dir, 1));

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-24">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-10 fade-in">
          <CalendarIcon size={40} className="text-gold mx-auto mb-6" strokeWidth={1} />
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">Réserver votre séance</p>
          <h1 className="text-5xl md:text-6xl mb-6 section-title"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Réservation
          </h1>
          <div className="gold-divider" />
        </div>

        {/* Step indicator */}
        {step !== "confirm" && (
          <div className="flex justify-center mb-10">
            <div className="flex items-center gap-3 text-xs uppercase tracking-wider">
              <StepDot active={step === "date"} done={step !== "date"} num="1" label="Date" />
              <div className="w-8 h-px bg-gold/30" />
              <StepDot active={step === "time"} done={step === "form"} num="2" label="Heure" />
              <div className="w-8 h-px bg-gold/30" />
              <StepDot active={step === "form"} done={false} num="3" label="Infos" />
            </div>
          </div>
        )}

        {step === "date" && (
          <div className="border border-white/10 bg-zinc-950/40 p-6 md:p-10">
            <div className="flex items-center justify-between mb-8">
              <button onClick={() => changeMonth(-1)} className="p-2 hover:text-gold text-white transition-colors"><ChevronLeft size={24} /></button>
              <h2 className="text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {monthNames[month]} <span className="text-gold">{year}</span>
              </h2>
              <button onClick={() => changeMonth(1)} className="p-2 hover:text-gold text-white transition-colors"><ChevronRight size={24} /></button>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-2">
              {dayNames.map((d, i) => <div key={`dn-${i}`} className="text-center text-gold text-xs tracking-wider uppercase py-2">{d}</div>)}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {days.map((date, i) => {
                if (!date) return <div key={`empty-${i}`} />;
                const fullyBlocked = isDayFullyBlocked(date);
                const availCount = !fullyBlocked ? getAvailableSlotsCount(date) : 0;
                const isFull = !fullyBlocked && availCount === 0;
                const blocked = fullyBlocked || isFull;
                const isToday = date.toDateString() === new Date().toDateString();
                return (
                  <button
                    key={date.toISOString().split('T')[0]}
                    onClick={() => handleSelectDate(date)}
                    disabled={blocked}
                    className={`calendar-day relative ${blocked ? "blocked" : "available"} ${isToday ? "ring-1 ring-gold" : ""}`}
                  >
                    {date.getDate()}
                    {!blocked && availCount > 0 && availCount < getTimeSlotsForDate(date).length && (
                      <span className="absolute bottom-0.5 right-1 text-[9px] text-gold">{availCount}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap gap-4 justify-center text-xs text-gray-400">
              <div className="flex items-center gap-2"><span className="w-3 h-3 bg-gold/20 border border-gold/40" />Disponible</div>
              <div className="flex items-center gap-2"><span className="w-3 h-3 bg-zinc-800 border border-zinc-700" />Complet / Indisponible</div>
            </div>

            <div className="mt-8 border-t border-white/10 pt-6">
              <div className="flex items-start gap-3 text-sm text-gray-400">
                <AlertCircle size={18} className="text-gold shrink-0 mt-0.5" />
                <p>
                  <strong className="text-white">Un acompte de 30% est demandé</strong> pour valider votre réservation. Ludovic vous contactera par téléphone pour convenir du lieu (studio, domicile, ou déplacement).
                </p>
              </div>
            </div>
          </div>
        )}

        {step === "time" && selectedDate && (
          <div className="border border-white/10 bg-zinc-950/40 p-6 md:p-10">
            <div className="mb-8 pb-6 border-b border-white/10">
              <p className="text-gold text-xs uppercase tracking-widest mb-2">Date sélectionnée</p>
              <h3 className="text-2xl md:text-3xl capitalize" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h3>
              <button type="button" onClick={() => setStep("date")} className="text-gold text-xs underline mt-2">Changer de date</button>
            </div>

            <h3 className="text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              <Clock size={18} className="inline-block mr-2 text-gold" />
              Choisissez un créneau horaire
            </h3>
            <p className="text-gray-400 text-xs mb-6">
              Plage disponible : {selectedDate.getMonth() >= 6 ? '6h' : '7h'} à 22h — tous les jours
              {selectedDate.getMonth() >= 6 && (
                <span className="text-gold/80 ml-1">(horaires été)</span>
              )}
            </p>

            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2">
              {getTimeSlotsForDate(selectedDate).map((slot) => {
                const available = isSlotAvailable(selectedDate, slot);
                const selected = selectedTime === slot;
                const slotClass = (() => {
                  if (selected) return "border-gold bg-gold text-black font-semibold";
                  if (available) return "border-white/20 text-white hover:border-gold hover:text-gold";
                  return "border-white/5 text-gray-600 bg-zinc-900 cursor-not-allowed line-through";
                })();
                return (
                  <button
                    key={slot}
                    onClick={() => available && setSelectedTime(slot)}
                    disabled={!available}
                    className={`py-3 text-sm tracking-wider border transition-colors ${slotClass}`}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setStep("date")} className="btn-gold flex-1 justify-center">Retour</button>
              <button
                onClick={() => selectedTime && setStep("form")}
                disabled={!selectedTime}
                className="btn-gold-filled flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continuer
              </button>
            </div>
          </div>
        )}

        {step === "form" && selectedDate && selectedTime && (
          <form onSubmit={handleSubmit} className="border border-white/10 bg-zinc-950/40 p-6 md:p-10">
            <div className="mb-8 pb-6 border-b border-white/10">
              <p className="text-gold text-xs uppercase tracking-widest mb-2">Votre créneau</p>
              <h3 className="text-2xl md:text-3xl capitalize" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} à <span className="text-gold">{selectedTime}</span>
              </h3>
              <button type="button" onClick={() => setStep("time")} className="text-gold text-xs underline mt-2">Modifier le créneau</button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-xs tracking-wider uppercase text-gray-400 mb-2">Prestation *</label>
                <select
                  value={form.prestation}
                  onChange={(e) => setForm({...form, prestation: e.target.value})}
                  className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
                >
                  {TARIFS.map(t => (
                    <option key={t.id} value={t.id}>{t.title} — {t.priceDisplay} (acompte {computeDeposit(t)}€)</option>
                  ))}
                </select>
              </div>

              {/* Deposit summary */}
              <div className="bg-gold/10 border border-gold/40 p-4 rounded">
                <p className="text-gold text-xs uppercase tracking-wider mb-2">Récapitulatif financier</p>
                <div className="flex justify-between text-sm text-gray-300 mb-1">
                  <span>Prestation :</span><span className="text-white">{selectedTarif?.price}€</span>
                </div>
                {giftCheck?.valid && giftCheck.amount >= (selectedTarif?.price || 0) && (
                  <div className="flex justify-between text-sm text-green-400 mb-1">
                    <span>🎁 Carte cadeau :</span><span>−{selectedTarif?.price}€</span>
                  </div>
                )}
                <div className="border-t border-gold/20 my-2" />
                {giftCheck?.valid && giftCheck.amount >= (selectedTarif?.price || 0) ? (
                  <>
                    <div className="flex justify-between text-base">
                      <span className="text-white font-medium">Acompte à régler :</span>
                      <span className="text-green-400 font-bold text-xl">0€ ✅</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                      <span>Solde le jour J :</span>
                      <span>0€ — tout est réglé</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-base">
                      <span className="text-white font-medium">Acompte à régler maintenant (30%) :</span>
                      <span className="text-gold font-bold text-xl">{deposit}€</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-400 mt-1">
                      <span>Solde le jour J :</span>
                      <span>{(selectedTarif?.price || 0) - deposit}€</span>
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RField label="Nom et prénom" value={form.name} onChange={(v) => setForm({...form, name: v})} required />
                <RField label="Téléphone" type="tel" value={form.phone} onChange={(v) => setForm({...form, phone: v})} required />
              </div>
              <RField label="Email" type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} required />

              <RField label="Code carte cadeau (si applicable)" value={form.giftCode} onChange={(v) => setForm({...form, giftCode: v.toUpperCase()})} placeholder="LG-XXXXXX-XXXX" />
              {giftCheck && form.giftCode && (
                <div className={`-mt-3 text-xs p-2.5 border ${
                  giftCheck.valid
                    ? "border-green-500/40 bg-green-500/10 text-green-300"
                    : "border-red-500/40 bg-red-500/10 text-red-300"
                }`}>
                  {giftCheck.valid && (
                    <>✅ Carte cadeau valide — <strong>{giftCheck.amount}€</strong> pour "{giftCheck.prestationTitle}".{" "}
                    {giftCheck.amount >= (selectedTarif?.price || 0)
                      ? <strong>Votre séance est couverte, aucun acompte requis.</strong>
                      : `Elle ne couvre que ${giftCheck.amount}€ sur ${selectedTarif?.price}€ — solde à régler.`}</>
                  )}
                  {!giftCheck.valid && giftCheck.pending && "⏳ Cette carte n'est pas encore activée (paiement en attente)."}
                  {!giftCheck.valid && giftCheck.used && "❌ Cette carte a déjà été utilisée."}
                  {!giftCheck.valid && !giftCheck.pending && !giftCheck.used && "❌ Code invalide."}
                </div>
              )}

              {!(giftCheck?.valid && giftCheck.amount >= (selectedTarif?.price || 0)) && (
              <div>
                <label className="block text-xs tracking-wider uppercase text-gray-400 mb-2">Mode de paiement de l'acompte *</label>
                <div className="grid grid-cols-2 gap-2">
                  {[{v:"virement",l:"Virement bancaire"},{v:"wero",l:"Wero"}].map(p => (
                    <button
                      key={p.v}
                      type="button"
                      data-testid={`payment-${p.v}-btn`}
                      onClick={() => setForm({...form, payment: p.v})}
                      className={`py-3 px-3 text-xs uppercase tracking-wider border transition-colors ${
                        form.payment === p.v ? "border-gold bg-gold text-black" : "border-white/20 text-white/70 hover:border-gold"
                      }`}
                    >
                      {p.l}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">Les coordonnées (IBAN ou n° Wero) vous seront envoyées par email sécurisé après validation.</p>
              </div>
              )}

              {giftCheck?.valid && giftCheck.amount >= (selectedTarif?.price || 0) ? (
                <div className="bg-green-500/5 border border-green-500/30 p-4 flex items-start gap-3">
                  <AlertCircle size={18} className="text-green-400 shrink-0 mt-0.5" />
                  <div className="text-xs text-gray-300 space-y-1">
                    <p><strong className="text-green-400">🎉 Votre séance sera confirmée instantanément grâce à votre carte cadeau.</strong></p>
                    <p>Votre créneau sera bloqué dès validation du formulaire.</p>
                    <p>📞 Ludovic vous contactera par téléphone pour confirmer le lieu de la séance (studio, domicile, ou déplacement).</p>
                  </div>
                </div>
              ) : (
                <div className="bg-red-500/5 border border-red-500/30 p-4 flex items-start gap-3">
                  <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
                  <div className="text-xs text-gray-300 space-y-1">
                    <p><strong className="text-white">⚠️ Votre créneau sera bloqué uniquement après réception de l'acompte de {deposit}€.</strong></p>
                    <p><strong className="text-red-400">Acompte non remboursable en cas d'annulation.</strong></p>
                    <p>📞 Ludovic vous contactera par téléphone pour convenir du lieu (studio, domicile, ou déplacement) et, si besoin, des éventuels frais de trajet.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={() => setStep("time")} className="btn-gold flex-1 justify-center">Retour</button>
              <button type="submit" disabled={submitting} className="btn-gold-filled flex-1 justify-center disabled:opacity-50">
                {submitting ? "Envoi..." : `Payer l'acompte de ${deposit}€`}
              </button>
            </div>
          </form>
        )}

        {step === "confirm" && booking && (
          <div className="border border-gold/40 bg-zinc-950/40 p-8 md:p-12 text-center">
            <div className="w-16 h-16 rounded-full border-2 border-gold mx-auto mb-6 flex items-center justify-center">
              <Check size={32} className="text-gold" />
            </div>
            <h2 className="text-4xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Réservation enregistrée !
            </h2>
            <div className="gold-divider mb-6" />
            <p className="text-gray-300 mb-6">Merci {booking.name}. Un email vient de vous être envoyé à <strong className="text-gold">{booking.email}</strong></p>

            <div className="border border-white/10 bg-black p-6 mb-6 text-left max-w-md mx-auto">
              <p className="text-gold text-xs uppercase tracking-widest mb-3">Récapitulatif</p>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-400">Numéro :</span> <strong className="text-gold font-mono">#{booking.ref}</strong></p>
                <p><span className="text-gray-400">Prestation :</span> {booking.prestationTitle}</p>
                <p><span className="text-gray-400">Date :</span> <span className="capitalize">{booking.dateFormatted}</span> à <strong>{booking.time}</strong></p>
                <p><span className="text-gray-400">Montant total :</span> {booking.amount}€</p>
                <p><span className="text-gray-400">Acompte à régler :</span> <strong className="text-gold">{booking.deposit}€</strong></p>
              </div>
            </div>

            <div className="border border-gold/30 bg-black p-6 mb-6 text-left max-w-md mx-auto">
              <div className="flex items-start gap-3 mb-3">
                <Check size={20} className="text-gold shrink-0 mt-0.5" />
                <p className="text-gold text-xs uppercase tracking-widest">Prochaine étape</p>
              </div>
              <p className="text-gray-200 text-sm leading-relaxed mb-3">
                <strong className="text-white">Consultez votre email</strong> pour régler l'acompte de <strong className="text-gold">{booking.deposit}€</strong> via {booking.payment.toUpperCase()}.
              </p>
              <p className="text-gray-400 text-xs italic">Votre créneau sera confirmé dès réception du paiement.</p>
            </div>

            <button onClick={() => navigate('/')} className="btn-gold-filled">Retour à l'accueil</button>
          </div>
        )}
      </div>
    </div>
  );
}

function StepDot({ active, done, num, label }) {
  const dotClass = active
    ? "bg-gold border-gold text-black"
    : (done ? "bg-gold/20 border-gold text-gold" : "border-white/30 text-white/40");
  return (
    <div className="flex items-center gap-2">
      <span className={`w-8 h-8 rounded-full flex items-center justify-center border ${dotClass}`}>
        {done ? <Check size={14} /> : num}
      </span>
      <span className={`${active ? "text-gold" : "text-white/50"}`}>{label}</span>
    </div>
  );
}

function RField({ label, type = "text", value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="block text-xs tracking-wider uppercase text-gray-400 mb-2">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-black border border-white/20 focus:border-gold focus:outline-none p-3 text-white"
      />
    </div>
  );
}
